import {
    IExamApplicationDetails,
    IExamApplicationList,
    ExamSessionDetails,
    IExamDetails,
    ExamApplicationStatus,
    ICreateExamApplicationByQec,
    CreateExamApplicationByQec,
    UpdateExamApplicationByQec,
    IUpdateExamApplicationByQec,
    ExamApplicationList,
    QualificationDocumentTemplateGroup,
    ISendCodeToApproveApplication,
    SendCodeToApproveApplication,
} from '../../api/ApiClient';
import { INotification } from 'components';
import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';

const initialState: IState = {
    examApplicationsList: null,
    applicationDetails: null,
    examSessionDetails: null,
    examDetails: null,
    draftsList: null,
    documentTemplateGroups: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setExamApplicationsList(state: IState, examApplicationsList: IExamApplicationList): IState {
            return { ...state, examApplicationsList };
        },
        setExamApplicationDetails(state: IState, applicationDetails: IExamApplicationDetails): IState {
            return { ...state, applicationDetails };
        },
        setSessionDetails(state, examSessionDetails: ExamSessionDetails) {
            return { ...(state || {}), examSessionDetails };
        },
        setExamDetails(state, examDetails: IExamDetails): IState {
            return { ...state, examDetails };
        },
        setDocumentTemplateGroups(state, documentTemplateGroups: QualificationDocumentTemplateGroup[] | null) {
            return { ...state, documentTemplateGroups };
        },
        setDrafts(state: IState, draftsList: ExamApplicationList | null): IState {
            return { ...state, draftsList };
        },
        clearExamApplications(state): IState {
            return { ...state, examApplicationsList: null };
        },
        clearApplicationDetails(state): IState {
            return { ...state, applicationDetails: null };
        },
    },
    effects: (dispatch: any) => ({
        async fetchExamApplicationsAsync(props: IFetchExamApplicationsProps) {
            const examApplicationsList = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplications(
                props.examId,
                props.searchField,
                props.searchTerm,
                props.page,
                props.pageSize || 100,
                props.sortOrder,
                props.sortField,
            ));

            this.setExamApplicationsList(examApplicationsList);
        },
        async fetchExamApplicationsPaymentRequiredAsync(props: IFetchExamApplicationsProps) {
            const examApplicationsList = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplicationsPaymentRequired(
                props.examId,
                props.searchField,
                props.searchTerm,
                props.page,
                props.pageSize || 100,
                props.sortOrder,
                props.sortField,
            ));

            this.setExamApplicationsList(examApplicationsList);
        },
        async fetchExamApplicationDetailsAsync(examApplicationId: string) {
            this.setExamApplicationDetails(null);
            const exam = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplication(examApplicationId));

            this.setExamApplicationDetails(exam);
        },
        async getExamSessionDetailsAsync(examApplicationId: string) {
            const sessionDetails = await apiConnector.execApi(dispatch, api => api.getExamSessionDetails(examApplicationId));
            this.setSessionDetails(sessionDetails);
        },
        async fetchExamDetailsAsync(id: string) {
            const examDetail = await apiConnector.execApi(dispatch, apiClient => apiClient.getExam(id));
            this.setExamDetails(examDetail);
        },
        async getExamApplicationDetailsAsync(examApplicationId: string) {
            this.setExamApplicationDetails(null);
            this.setSessionDetails(null);

            const examApplication = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplication(examApplicationId));
            this.setExamApplicationDetails(examApplication as IExamApplicationDetails);

            if (!examApplication) {
                this.setExamDetails(null);
            } else {
                const examDetail = await apiConnector.execApi(dispatch, apiClient => apiClient.getExam(examApplication.exam.id));
                this.setExamDetails(examDetail);
            }

            if (examApplication && (examApplication.status === ExamApplicationStatus.Passed || examApplication.status === ExamApplicationStatus.NotPassed)) {
                const sessionDetails = await apiConnector.execApi(dispatch, api => api.getExamSessionDetails(examApplicationId));
                this.setSessionDetails(sessionDetails);
            }
        },
        async getDraftsListAsync(payload: IGetDraftsList) {
            const drafts = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamApplicationDrafts(
                payload.searchField,
                payload.searchTerm,
                payload.page,
                payload.pageSize,
                payload.sortOrder,
                payload.sortField,
            ));
            this.setDrafts(drafts);
        },
        async createExamApplicationDraftAsync(body: ICreateExamApplicationByQec) {
            const applicationDetails = await apiConnector.execApi(dispatch, apiClient => apiClient.createExamApplicationDraft(new CreateExamApplicationByQec(body)));
            if (applicationDetails) {
                dispatch.notifications.addNotification([{ message: 'Черновик успешно добавлен!', appearance: 'success' }] as INotification[]);
            }

            this.setExamApplicationDetails(applicationDetails);

            return applicationDetails;
        },
        async updateExamApplicationDraftAsync(data: IUpdateDraft) {
            const applicationDetails = await apiConnector.execApi(dispatch, apiClient => apiClient.updateExamApplicationDraft(
                data.examApplicationId,
                new UpdateExamApplicationByQec(data.body),
            ));

            if (applicationDetails) {
                this.setExamApplicationDetails(applicationDetails);
                dispatch.notifications.addNotification([{ message: 'Черновик успешно обновлен!', appearance: 'success' }] as INotification[]);
            }

            return applicationDetails;
        },
        async deleteExamApplicationDraftAsync(examApplicationId: string) {
            const applicationDetails = await apiConnector.execApi(dispatch, apiClient => apiClient.deleteExamApplicationDraft(examApplicationId));
            if (applicationDetails) {
                dispatch.notifications.addNotification([{ message: 'Черновик успешно удален!', appearance: 'success' }] as INotification[]);
                this.setExamApplicationDetails(null);
            }

            return applicationDetails;
        },
        async uploadExamApplicationDocument(payload: UploadDocumentPayload) {
            const applicationDetails = await apiConnector.execApi(dispatch, api => api.uploadExamApplicationDocumentFix(
                payload.examApplicationId,
                payload.templateId,
                payload.file,
            ));

            if (applicationDetails) {
                this.setExamApplicationDetails(applicationDetails);
            }
        },
        async deleteExamApplicationDocument(payload: IDeleteDocument) {
            const applicationDetails = await apiConnector.execApi(dispatch, api => api.deleteExamApplicationDocument(
                payload.examApplicationId,
                payload.documentId,
            ));

            if (applicationDetails) {
                this.setExamApplicationDetails(applicationDetails);
            }
        },
        async deleteExamApplicationDocumentSourceAsync(payload: DeleteDocumentSource) {
            const applicationDetails = await apiConnector.execApi(dispatch, api => api.deleteExamApplicationDocumentSource(
                payload.examApplicationId,
                payload.documentId,
                payload.sourceFileId,
            ));

            if (applicationDetails) {
                this.setExamApplicationDetails(applicationDetails);
            }
        },
        async submitExamApplication(id: string) {
            const applicationDetails = await apiConnector.execApi(dispatch, api => api.submitExamApplication(id));

            if (applicationDetails) {
                dispatch.notifications.addNotification([{ message: 'Заявка успешно отправлена на рассмотрение!', appearance: 'success' }] as INotification[]);
            }

            this.setExamApplicationDetails(applicationDetails);

            return applicationDetails;
        },
        async getDocumentTemplateGroupsAsync(qualificationId: string) {
            const documentTemplateGroups = await apiConnector.execApi(dispatch, apiClient => apiClient.getDocumentTemplateGroups(qualificationId));

            this.setDocumentTemplateGroups(documentTemplateGroups);

            return documentTemplateGroups;
        },
        async sendCodeToApproveApplicationAsync(data: ISendCodeToApproveApplicationProps) {
            const verificationCodeSendingStatus = await apiConnector.execApi(dispatch, api => api.sendCodeToApproveApplication(
                data.examApplicationId,
                new SendCodeToApproveApplication(data.body),
            ));

            return verificationCodeSendingStatus;
        },
        async admitToExamAsync(examApplicationId: string) {
            const admit = await apiConnector.execApi(dispatch, apiClient => apiClient.admitToExam(examApplicationId));

            return admit;
        },
    }),
});

export interface IFetchExamApplicationsProps {
    examId?: string;
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

interface ISendCodeToApproveApplicationProps {
    examApplicationId: string;
    body: ISendCodeToApproveApplication;
}

interface IState {
    examApplicationsList: IExamApplicationList | null;
    applicationDetails: IExamApplicationDetails | null;
    examSessionDetails: ExamSessionDetails | null;
    examDetails: IExamDetails | null;
    draftsList: ExamApplicationList | null;
    documentTemplateGroups: QualificationDocumentTemplateGroup[] | null;
}

interface UploadDocumentPayload {
    examApplicationId: string;
    templateId: string;
    file: File;
}

interface DeleteDocumentSource {
    examApplicationId: string;
    documentId: string;
    sourceFileId: string;
}

interface IDeleteDocument {
    examApplicationId: string;
    documentId: string;
}

interface IUpdateDraft {
    examApplicationId: string;
    body: IUpdateExamApplicationByQec;
}

interface IGetDraftsList {
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}
